/**
 * Centralized error toast with analytics.
 *
 * Every caller gets: the generic "what happened" message in the UI + the
 * real error detail in a sub-line (so users can self-serve if the error
 * is actionable) + an analytics record so we can see what's failing in
 * production. Stops the "generic Failed to load that tells us nothing"
 * anti-pattern.
 *
 * Usage:
 *
 *   import { toastError } from "@/lib/toast-error";
 *   ...
 *   catch (err) {
 *     toastError("Couldn't start your session", err);
 *   }
 */
import { toast } from "sonner";
import * as Sentry from "@sentry/react";

type AnyError = Error | { message?: string; error?: string } | string | unknown;

function extractMessage(err: AnyError): string | null {
  if (!err) return null;
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object") {
    const obj = err as Record<string, unknown>;
    if (typeof obj.error === "string") return obj.error;
    if (typeof obj.message === "string") return obj.message;
  }
  return null;
}

export interface ToastErrorOptions {
  /** Tag the error with a source (e.g. "library.delete"). Helps funnel analytics. */
  source?: string;
  /** Hide the raw error detail from the UI (still logged). Default: show it. */
  suppressDetail?: boolean;
}

export function toastError(
  headline: string,
  err?: AnyError,
  opts: ToastErrorOptions = {},
) {
  const detail = extractMessage(err);
  const description = opts.suppressDetail ? undefined : detail ?? undefined;

  toast.error(headline, {
    description,
  });

  // Send to Sentry so we see it in the error feed even if PostHog isn't
  // configured. Sentry is initialized with noop in dev if DSN missing.
  if (err instanceof Error) {
    Sentry.captureException(err, {
      tags: { source: opts.source ?? "unknown" },
      extra: { headline },
    });
  } else if (detail) {
    Sentry.captureMessage(`${headline}: ${detail}`, {
      level: "error",
      tags: { source: opts.source ?? "unknown" },
    });
  }

  // Dev log for debugging.
  if (import.meta.env.DEV) {
    console.error(`[toastError] ${headline}`, { detail, err });
  }
}
